/**
 * The page that stops a link preview from spending someone's reset link.
 *
 * Supabase recovery links are single use. Anything that fetches one consumes
 * it, and the modern internet fetches everything: paste a link into Instagram,
 * Slack, iMessage, WhatsApp or Teams and a crawler loads it within seconds to
 * build the little preview card. By the time the human taps it, the token is
 * spent and they get "this reset link has expired or is invalid", which is
 * both true and completely baffling to them.
 *
 * That is exactly what happened the first time an admin sent one of these
 * through an Instagram DM. The preview card in the thread was the receipt.
 *
 * So an admin-generated link points here instead, carrying the real Supabase
 * link as a parameter. This page does nothing on load: no fetch, no redirect,
 * no Supabase call. A crawler that loads it gets a static page and spends
 * nothing. Only a real press on the button navigates to the real link, and
 * that is the first time the token is touched.
 *
 * It follows that this page must never auto-navigate, however convenient that
 * would feel. The press IS the mechanism.
 *
 * No em dashes.
 */
import React, { useEffect, useState } from "react";
import { KeyRound, ArrowRight } from "lucide-react";
import { GOLD } from "../dashboard/constants/theme";
import { BAM_LOGO_PNG } from "../dashboard/constants/branding";

/** The wrapped link an admin sends. Exported so the API and tests agree. */
export const HANDOFF_PARAM = "open_reset";

export function buildHandoffUrl(origin, actionLink) {
  return `${origin.replace(/\/$/, "")}/?${HANDOFF_PARAM}=${encodeURIComponent(actionLink)}`;
}

/** The Supabase link carried in the URL, or null when it is missing or junk. */
export function readHandoffTarget(search) {
  try {
    const raw = new URLSearchParams(search).get(HANDOFF_PARAM);
    if (!raw) return null;
    const url = new URL(raw);
    // Only ever hand off to Supabase's own verify endpoint. Without this the
    // parameter is an open redirect with our domain's name on it.
    if (url.protocol !== "https:") return null;
    if (!/(^|\.)supabase\.(co|in)$/i.test(url.hostname)) return null;
    return url.toString();
  } catch {
    return null;
  }
}

export default function ResetHandoff() {
  const [target, setTarget] = useState(null);

  useEffect(() => {
    setTarget(readHandoffTarget(window.location.search));
    // Crawlers ignore this, but it costs nothing and states the intent.
    const m = document.createElement("meta");
    m.name = "robots";
    m.content = "noindex, nofollow";
    document.head.appendChild(m);
    return () => { try { document.head.removeChild(m); } catch {} };
  }, []);

  const go = () => { if (target) window.location.href = target; };

  return (
    <div style={{
      position: "fixed", inset: 0, background: "#080808", color: "#F5F0E8",
      fontFamily: "'DM Sans', sans-serif", display: "flex", alignItems: "center",
      justifyContent: "center", padding: 20,
    }}>
      <div style={{ width: "100%", maxWidth: 400, textAlign: "center" }}>
        <img src={BAM_LOGO_PNG} alt="BAM" style={{
          width: 48, height: 60, objectFit: "contain", marginBottom: 18,
          filter: "drop-shadow(0 0 20px rgba(226,221,159,0.3))",
        }}/>
        <div style={{
          fontFamily: "'Bebas Neue', sans-serif", fontSize: 32, letterSpacing: 1.6,
          marginBottom: 10,
        }}>
          SET YOUR PASSWORD
        </div>

        {target ? (
          <>
            <p style={{ fontSize: 13.5, lineHeight: 1.65, color: "#9A9A9A", margin: "0 0 22px" }}>
              Press the button to open your reset link. It works once, so open
              it when you are ready to choose a new password.
            </p>
            <button onClick={go}
              style={{
                width: "100%", display: "inline-flex", alignItems: "center",
                justifyContent: "center", gap: 8, padding: "14px 18px",
                borderRadius: 11, border: "none", background: GOLD, color: "#111",
                fontSize: 14.5, fontWeight: 800, cursor: "pointer", fontFamily: "inherit",
              }}>
              <KeyRound size={16}/> Continue <ArrowRight size={15}/>
            </button>
          </>
        ) : (
          <>
            <p style={{ fontSize: 13.5, lineHeight: 1.65, color: "#9A9A9A", margin: "0 0 22px" }}>
              This link is missing the part that identifies you. Ask for a new
              one, or reset it yourself from the sign in screen.
            </p>
            <a href="/#/signin"
              style={{
                display: "inline-block", padding: "12px 20px", borderRadius: 11,
                background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)",
                color: "#F5F0E8", fontSize: 13.5, fontWeight: 700, textDecoration: "none",
              }}>
              Back to sign in
            </a>
          </>
        )}
      </div>
    </div>
  );
}
